// @ts-nocheck
import { motion } from 'framer-motion';
import { Text, XStack, YStack } from 'tamagui';

import { useIsMobile } from '../../hooks/useIsMobile';
import { useMotion } from '../../hooks/useMotion';
import { APPS } from './constants';

const ANIM_CSS = `
  @keyframes flowOut {
    from { stroke-dashoffset: 16; }
    to { stroke-dashoffset: 0; }
  }
  @keyframes flowIn {
    from { stroke-dashoffset: 0; }
    to { stroke-dashoffset: 16; }
  }
  .line-outbound {
    stroke-dasharray: 4 12;
    animation: flowOut 1.5s linear infinite;
  }
  .line-inbound {
    stroke-dasharray: 4 12;
    animation: flowIn 1.5s linear infinite;
  }
`;

// Positions for 8 apps — tighter orbit with room for curved lines
const POSITIONS = [
  { x: 450, y: 80 },   // top
  { x: 650, y: 130 },  // top-right
  { x: 700, y: 280 },  // right
  { x: 650, y: 430 },  // bottom-right
  { x: 450, y: 480 },  // bottom
  { x: 250, y: 430 },  // bottom-left
  { x: 200, y: 280 },  // left
  { x: 250, y: 130 },  // top-left
];

const CENTER = { x: 450, y: 280 };

function DesktopApps() {
  return (
    <svg
      width="100%"
      height="560"
      viewBox="0 0 900 560"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ maxWidth: 900 }}
    >
      <style>{ANIM_CSS}</style>

      {/* Dual spline connections — outbound (query) + inbound (results) */}
      {POSITIONS.map((pos, i) => {
        const dx = pos.x - CENTER.x;
        const dy = pos.y - CENTER.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        // Perpendicular unit vector (for parallel line offset AND curve bowing)
        const perpX = -dy / len;
        const perpY = dx / len;
        // Parallel line offset (3px apart)
        const nx = perpX * 3;
        const ny = perpY * 3;
        // Curve bowing — push control points perpendicular to the connection
        const curvature = 18 + (i % 3) * 6;
        const sign = i % 2 === 0 ? 1 : -1;
        const bowX = perpX * curvature * sign;
        const bowY = perpY * curvature * sign;
        const cx1 = CENTER.x + dx * 0.33 + bowX;
        const cy1 = CENTER.y + dy * 0.33 + bowY;
        const cx2 = CENTER.x + dx * 0.66 + bowX * 0.6;
        const cy2 = CENTER.y + dy * 0.66 + bowY * 0.6;

        const makeD = (ox: number, oy: number) =>
          `M ${CENTER.x + ox} ${CENTER.y + oy} C ${cx1 + ox} ${cy1 + oy}, ${cx2 + ox} ${cy2 + oy}, ${pos.x + ox} ${pos.y + oy}`;

        return (
          <g key={`line-${i}`}>
            {/* Static base curves */}
            <path d={makeD(nx, ny)} stroke="var(--color4)" strokeWidth="1" fill="none" />
            <path d={makeD(-nx, -ny)} stroke="var(--color4)" strokeWidth="1" fill="none" />
            {/* Outbound — query flows center → app */}
            <path
              d={makeD(nx, ny)}
              stroke="var(--accentBackground)" strokeWidth="1.5" fill="none"
              className="line-outbound"
              style={{ animationDelay: `${i * 0.2}s` }}
            />
            {/* Inbound — results flow app → center */}
            <path
              d={makeD(-nx, -ny)}
              stroke="var(--color7)" strokeWidth="1" fill="none"
              className="line-inbound"
              style={{ animationDelay: `${i * 0.2}s` }}
            />
          </g>
        );
      })}

      {/* Center node — Search */}
      <circle cx={CENTER.x} cy={CENTER.y} r="52" fill="var(--color2)" stroke="var(--accentBackground)" strokeWidth="2" />
      <circle cx={CENTER.x} cy={CENTER.y} r="52" fill="var(--accentBackground)" opacity="0.06" />
      <text x={CENTER.x} y={CENTER.y - 6} textAnchor="middle" fill="var(--accentBackground)" fontSize="13" fontWeight="600">Unified</text>
      <text x={CENTER.x} y={CENTER.y + 12} textAnchor="middle" fill="var(--accentBackground)" fontSize="13" fontWeight="600">Search</text>

      {/* App nodes */}
      {APPS.map((app, i) => {
        const pos = POSITIONS[i];
        return (
          <g key={app.name}>
            {/* Connection port dot */}
            <circle cx={pos.x} cy={pos.y} r="3" fill="var(--accentBackground)" opacity="0.5">
              <animate attributeName="r" values="2.5;4;2.5" dur="2.5s" begin={`${i * 0.3}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.4;0.8;0.4" dur="2.5s" begin={`${i * 0.3}s`} repeatCount="indefinite" />
            </circle>
            {/* App card background */}
            <rect
              x={pos.x - 65} y={pos.y - 28}
              width="130" height="56" rx="8"
              fill="var(--color2)" stroke="var(--color4)" strokeWidth="1"
            />
            <text
              x={pos.x} y={pos.y + 5}
              textAnchor="middle" fill="var(--color12)"
              fontSize="13" fontWeight="600"
            >
              {app.name}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function MobileApps() {
  return (
    <YStack gap="$3" width="100%">
      {/* Search hub */}
      <YStack
        alignItems="center"
        justifyContent="center"
        padding="$4"
        borderRadius="$4"
        borderWidth={2}
        borderColor="$accentBackground"
        backgroundColor="$color2"
      >
        <Text fontSize={15} fontWeight="600" color="$accentBackground">
          Unified Search
        </Text>
        <Text fontSize={12} color="$color8" marginTop="$1">
          Everything connects here
        </Text>
      </YStack>

      {/* Connecting line */}
      <YStack alignSelf="center" width={1} height={24} backgroundColor="$color4" />

      {/* App grid */}
      <XStack flexWrap="wrap" gap="$3">
        {APPS.map((app) => (
          <YStack
            key={app.name}
            width="calc(50% - 6px)"
            padding="$3"
            borderRadius="$3"
            borderWidth={1}
            borderColor="$borderColor"
            backgroundColor="$color2"
            gap="$1"
          >
            <XStack alignItems="center" gap="$2">
              <app.icon size={14} color="var(--color9)" />
              <Text fontSize={13} fontWeight="600" color="$color12">{app.name}</Text>
            </XStack>
            <Text fontSize={12} color="$color8" lineHeight={18}>{app.description}</Text>
          </YStack>
        ))}
      </XStack>
    </YStack>
  );
}

export function Apps() {
  const isMobile = useIsMobile();
  const { sectionReveal, fadeReveal } = useMotion();

  return (
    <YStack
      id="apps"
      paddingVertical={isMobile ? 80 : 120}
      paddingHorizontal="$5"
      maxWidth={1100}
      alignSelf="center"
      width="100%"
      backgroundColor="$background"
    >
      <motion.div
        {...sectionReveal}
        viewport={{ once: true }}
      >
        <YStack gap="$4" marginBottom={isMobile ? '$6' : '$8'}>
          <Text
            fontSize={isMobile ? 32 : 44}
            fontWeight="700"
            letterSpacing={-1.5}
            lineHeight={isMobile ? 38 : 52}
            color="$color12"
          >
            Built-in apps
          </Text>
          <Text fontSize={isMobile ? 16 : 18} color="$color8" maxWidth={480} lineHeight={26}>
            Every app connects through one unified search layer. Find anything, from anywhere.
          </Text>
        </YStack>
      </motion.div>

      <motion.div
        {...fadeReveal}
        viewport={{ once: true }}
      >
        <YStack
          backgroundColor="$color1"
          borderRadius="$4"
          borderWidth={1}
          borderColor="$borderColor"
          padding={isMobile ? '$4' : '$6'}
          alignItems="center"
          overflow="hidden"
        >
          {isMobile ? <MobileApps /> : <DesktopApps />}
        </YStack>
      </motion.div>
    </YStack>
  );
}
